import axios, { AxiosPromise, CancelToken } from 'axios';
import { ApPlan } from '../sic-lib/types/ApPlan';
import {
    ApTemplate,
    AssignTemplateAsDraftRequest,
    CreateApTemplateRequest,
    UpdateApTemplateRequest,
} from '../sic-lib/types/ApTemplate';
import {
    ApTemplateAction,
    CreateApTemplateActionRequest,
    UpdateApTemplateActionRequest,
} from '../sic-lib/types/ApTemplateAction';
import { apiInstance as instance } from './';

/**
 * Makes a HTTP request to retrieve client's action plan templates
 * @param cancelToken
 */
export function requestApTemplates(cancelToken: CancelToken): AxiosPromise<ApTemplate[]> {
    return instance.get('/actionplantemplates', {
        cancelToken: cancelToken,
    });
}

/**
 * Make a HTTP request to retrieve an action plan template based on its id
 * @param templateId
 * @param cancelToken
 */
export function requestApTemplateById(
    templateId: number,
    cancelToken: CancelToken,
): AxiosPromise<ApTemplate> {
    return instance.get(`/actionplantemplates/${templateId}`, {
        cancelToken: cancelToken,
    });
}

/**
 * Make a HTTP request to create a new action plan template
 * @param data
 * @param cancelToken
 */
export function requestCreateApTemplate(
    data: CreateApTemplateRequest,
    cancelToken: CancelToken,
): AxiosPromise<ApTemplate> {
    return instance.post('/actionplantemplates', data, {
        cancelToken: cancelToken,
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Make a HTTP request to update an existing action plan template matching the specified template id
 * @param templateId
 * @param data
 * @param cancelToken
 */
export function requestUpdateApTemplate(
    templateId: number,
    data: UpdateApTemplateRequest,
    cancelToken: CancelToken,
): AxiosPromise<unknown> {
    return instance.put(`/actionplantemplates/${templateId}`, data, {
        cancelToken: cancelToken,
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Make a HTTP request to delete an action plan template matching the specified template id
 * @param templateId
 * @param cancelToken
 */
export function requestDeleteApTemplate(
    templateId: number,
    cancelToken: CancelToken,
): AxiosPromise<unknown> {
    return instance.delete(`/actionplantemplates/${templateId}`, {
        cancelToken: cancelToken,
    });
}

/**
 * Make a HTTP request to retreive an action plan tempalte's action matching the given template and action id
 * @param templateId
 * @param actionId
 * @param cancelToken
 */
export function requestApTemplateActionById(
    templateId: number,
    actionId: number,
    cancelToken: CancelToken,
): AxiosPromise<ApTemplateAction> {
    return instance.get(`/actionplantemplates/${templateId}/actions/${actionId}`, {
        cancelToken: cancelToken,
    });
}

/**
 * Make a HTTP request to create a new action in an existing an action plan template action that matches the given template id
 * @param templateId
 * @param data
 * @param cancelToken
 */
export function requestCreateApTemplateAction(
    templateId: number,
    data: CreateApTemplateActionRequest,
    cancelToken: CancelToken,
): AxiosPromise<ApTemplateAction> {
    return instance.post(`/actionplantemplates/${templateId}/actions`, data, {
        cancelToken: cancelToken,
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Make a HTTP request to update an existing action plan template's action matching the given template and action id
 * @param templateId
 * @param actionId
 * @param data
 * @param cancelToken
 */
export function requestUpdateApTemplateAction(
    templateId: number,
    actionId: number,
    data: UpdateApTemplateActionRequest,
    cancelToken: CancelToken,
): AxiosPromise<unknown> {
    return instance.put(`/actionplantemplates/${templateId}/actions/${actionId}`, data, {
        cancelToken: cancelToken,
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Make a HTTP request to delete an action from an existing action plan template matching the given template and action id
 * @param templateId
 * @param actionId
 * @param cancelToken
 */
export function requestDeleteApTemplateAction(
    templateId: number,
    actionId: number,
    cancelToken: CancelToken,
): AxiosPromise<unknown> {
    return instance.delete(`/actionplantemplates/${templateId}/actions/${actionId}`, {
        cancelToken: cancelToken,
    });
}

/**
 * Make a HTTP request to reorder the actions of the action plan template specified by the template Id
 * @param templateId
 * @param actionId
 * @param isUp
 * @param cancelToken
 */
export function requestSortApTemplateAction(
    templateId: number,
    actionId: number,
    isUp: boolean,
    cancelToken: CancelToken,
): AxiosPromise<unknown> {
    return instance.post(
        `/actionplantemplates/${templateId}/actions/${actionId}/changesort/${isUp}`,
        {
            cancelToken: cancelToken,
        },
    );
}

let requestAssignTemplateAsDraft_cancel: any;
/**
 * Make a HTTP request to assign an action plan template to a list of contacts as an action plan
 * @param templateId
 * @param data
 * @param cancelToken
 */
export function requestAssignTemplateAsDraft(
    templateId: number,
    data: AssignTemplateAsDraftRequest,
    //cancelToken: CancelToken
): AxiosPromise<ApPlan[]> {
    if (requestAssignTemplateAsDraft_cancel) requestAssignTemplateAsDraft_cancel();

    return instance.post(`/actionplantemplates/${templateId}/assigndraft`, data, {
        //cancelToken: cancelToken,
        cancelToken: new axios.CancelToken(function executor(c) {
            requestAssignTemplateAsDraft_cancel = c;
        }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}
